import React from "react";
import AboutDetails from "./AboutDetails";
import StepsSection from "./StepsSection";
import AboutSection from "./AboutSection";
import { Helmet } from "react-helmet";

const About = () => {
  return (
    <>
      <Helmet>

        {/* ================= PRIMARY SEO ================= */}

        <title>
          About Seo Up Rank | AI SEO Platform for Website Optimization
        </title>

        <meta
          name="description"
          content="Seo Up Rank is an AI powered SEO platform that automates website optimization, keyword research, technical SEO audits and ranking improvements."
        />

        <meta
          name="keywords"
          content="AI SEO tool, SEO platform, website optimization, keyword research tool, technical SEO audit, backlink analysis, Seo Up Rank"
        />

        <meta name="author" content="Seo Up Rank Team" />
        <meta name="robots" content="index, follow, max-image-preview:large" />

        {/* ================= CANONICAL ================= */}

        <link rel="canonical" href="https://novarsis.ai/about" />

        {/* ================= THEME COLOR ================= */}

        <meta name="theme-color" content="#008300" />

        {/* ================= OPEN GRAPH ================= */}

        <meta
          property="og:title"
          content="About Seo Up Rank | AI Powered SEO Platform"
        />

        <meta
          property="og:description"
          content="Discover Seo Up Rank, an AI SEO platform designed to automate website optimization, keyword research and ranking growth."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://novarsis.ai/about" />

        <meta
          property="og:image"
          content="https://novarsis.ai/seo-banner.png"
        />

        <meta property="og:site_name" content="Seo Up Rank" />

        {/* ================= TWITTER SEO ================= */}

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="About Seo Up Rank | AI SEO Platform"
        />

        <meta
          name="twitter:description"
          content="AI powered SEO tools for website optimization, keyword research, technical SEO audits and ranking improvements."
        />

        <meta
          name="twitter:image"
          content="https://novarsis.ai/seo-banner.png"
        />

        {/* ================= ORGANIZATION SCHEMA ================= */}

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Seo Up Rank",
            "url": "https://novarsis.ai",
            "logo": "https://novarsis.ai/logo.png",
            "description": "Seo Up Rank is an AI powered SEO platform for website optimization, keyword research, backlink analysis and technical SEO audits.",
            "sameAs": [
              "https://facebook.com/seouprank",
              "https://twitter.com/seouprank",
              "https://linkedin.com/company/seouprank"
            ]
          }
          `}
        </script>

        {/* ================= SOFTWARE APPLICATION SCHEMA ================= */}

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Seo Up Rank",
            "applicationCategory": "SEO Tool",
            "operatingSystem": "Web",
            "url": "https://novarsis.ai",
            "description": "AI powered SEO automation platform for website optimization, keyword research and ranking improvements."
          }
          `}
        </script>

        {/* ================= WEBPAGE SCHEMA ================= */}

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "About Seo Up Rank",
            "url": "https://novarsis.ai/about",
            "description": "Learn about Seo Up Rank, an AI powered SEO platform designed to help websites improve rankings and grow traffic."
          }
          `}
        </script>

        {/* ================= BREADCRUMB SCHEMA ================= */}

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://novarsis.ai"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "About",
                "item": "https://novarsis.ai/about"
              }
            ]
          }
          `}
        </script>

      </Helmet>

      {/* ================= HERO SECTION ================= */}

      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">

        {/* BACKGROUND VIDEO */}

        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/Images/BANNER_VIDEO.mp4"
          autoPlay
          loop
          muted
          playsInline
          poster="/Images/BANNER-IMAGE.png"
        />

        {/* IMAGE FALLBACK FOR SEO */}

        <img
          src="/Images/BANNER-IMAGE.png"
          alt="Seo Up Rank AI SEO platform banner"
          className="hidden"
        />

        {/* DARK OVERLAY */}

        <div className="absolute inset-0 bg-black/60"></div>

        {/* ================= CONTENT ================= */}

        <div className="relative z-10 text-center px-6 max-w-4xl">

          <h1 className="text-4xl md:text-6xl font-bold text-white">
            About <span className="text-[#008300]">Seo Up Rank AI</span> SEO
          </h1>

          <h2 className="sr-only">
            AI powered SEO automation platform for website optimization
          </h2>

          <p className="mt-4 text-lg md:text-xl text-gray-200">
            We’re redefining SEO through AI innovation, precision analytics,
            and future-ready optimization.
          </p>

        </div>

      </section>

      {/* ================= HIDDEN KEYWORDS ================= */}

      <div className="sr-only">
        AI SEO tool, website optimization platform, automated SEO audit,
        keyword research software, technical SEO analysis tool, AI powered SEO
        automation platform.
      </div>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}

      <div className="sr-only">
        <a href="/features">SEO Features</a>
        <a href="/tools">SEO Tools</a>
        <a href="/pricing">SEO Pricing</a>
        <a href="/blog">SEO Blog</a>
      </div>

      {/* ================= PAGE CONTENT ================= */}

      <AboutSection />
      <AboutDetails />
      <StepsSection />

    </>
  );
};

export default About;